import {
  Badge,
  Button,
  Divider,
  Group,
  Highlight,
  Image,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { Link, NavLink, useLoaderData, useParams, useRouteLoaderData } from "@remix-run/react";
import {
  IconBriefcase,
  IconStarFilled,
  IconTrophyFilled,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import placeholderPerson from "~/assets/misc/placeholder-person.png";
import { getCountryNameFromCode } from "~/data/dropdown-values";
import { TranslationKey, useTranslation } from "~/hooks/use-typed-translation";
import { DoctorProfile } from "~/types/index";
import { abbreviateMiddleNames } from "~/utils/abbreviate-middle-names";
import { formatCurrency } from "~/utils/format-currency";

import classes from "./doctor-card.module.scss";

interface DoctorCardProps {
  doctor: DoctorProfile;
  search?: string;
  currency?: string;
}

const formatAvailability = (
  availability: string,
  locale: string,
  useLocalTimeZone: boolean
) => {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  // Match the server snapshot during hydration, then use the visitor's timezone.
  if (!useLocalTimeZone) options.timeZone = "UTC";

  return new Intl.DateTimeFormat(locale, options)
    .format(new Date(availability))
    .replace(/[\u00a0\u202f]/g, " ");
};

const cleanLabel = (value?: string | null) =>
  value?.replace(/\s+/g, " ").trim();

export const DoctorCard = ({
  doctor,
  search,
  currency: currencyOverride,
}: DoctorCardProps) => {
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData("root") as any;
  const currency = currencyOverride || rootData?.currency || "XOF";

  const { slug: partnerSlug } = useParams();
// ...


  const [isHydrated, setIsHydrated] = useState(false);
  const [specialties, setSpecialties] = useState(doctor?.specialties ?? []);
  const ratingSummary = doctor?.ratingSummary;
  const shouldShowRating =
    typeof ratingSummary?.average === "number" && (ratingSummary?.count ?? 0) >= 3;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Calculate lowest price from services
  const lowestPrice = useMemo(() => {
    if (!doctor?.services || doctor.services.length === 0) return null;
    const prices = doctor.services
      .map(service => service.price)
      .filter((price): price is number => typeof price === 'number' && price > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  }, [doctor?.services]);

  // Get country names from ISO codes or show "Worldwide"
  const licensedCountryNames = useMemo(() => {
    if (!doctor?.licensedCountries || doctor.licensedCountries.length === 0) {
      return t("worldwide");
    }
    // Map ISO codes to full country names
    return doctor.licensedCountries
      .map(code => getCountryNameFromCode(code, t, i18n.language))
      .join(", ");
  }, [doctor?.licensedCountries, t, i18n.language]);

  // Format next availability date with timezone localization
  const nextAvailabilityFormatted = useMemo(() => {
    if (!doctor?.nextAvailability) return null;
    return formatAvailability(
      doctor.nextAvailability,
      i18n.language,
      isHydrated
    );
  }, [doctor?.nextAvailability, i18n.language, isHydrated]);

  useEffect(() => {
    if (search && specialties.length) {
      setSpecialties(prevSpecialties => {
        // Split the array into matches and non-matches
        const [matches, nonMatches] = prevSpecialties.reduce(
          ([matches, nonMatches], specialty) => {
            const list = specialty.toLowerCase().includes(search)
              ? matches
              : nonMatches;
            list.push(specialty);
            return [matches, nonMatches];
          },
          [[], []] as [string[], string[]]
        );

        return [
          ...matches.slice(0, 3), // Ensure matches appear in the first 3 positions
          ...matches.slice(3), // Add remaining matches
          ...nonMatches, // Add non-matching items
        ];
      });
    }
  }, [search]);

  return (
    <Paper
      component={NavLink}
      reloadDocument
      to={
        partnerSlug
          ? `${doctor?.slug ?? "#"}`
          : `/${i18n.language}/doctor/${doctor?.slug ?? "#"}`
      }
      // title={doctor?.name}
      radius={"md"}
      px={"sm"}
      pt={"md"}
      pb="xs"
      w={{ base: "100%", sm: "48%" }}
      withBorder
      aria-label={[
        cleanLabel(doctor?.name),
        cleanLabel(doctor?.specialization),
      ].filter(Boolean).join(" - ")}
      className={classes["doctor-card"]}>
      {({ isTransitioning }) => (
        <>
          <Group
            align="center"
            gap={"sm"}
            // style={
            //   isTransitioning ? { viewTransitionName: "doctor" } : undefined
            // }
          >
            <Image
              src={
                doctor?.image ??
                // `https://i.pravatar.cc/70?img=${Math.floor(Math.random() * 70)}`
                placeholderPerson
              }
              alt={doctor?.name}
              h={{ base: 60, sm: 60, md: 70 }}
              w={{ base: 60, sm: 60, md: 70 }}
              width={70}
              miw={70}
              height={70}
              radius={"100vmax"}
            />

            <Stack gap={8} w="fit-content">
              <Highlight
                highlight={search || ""}
                fz={15}
                fw={600}
                ml={3}
                w="fit-content"
                className={classes["doctor-name"]}>
                {abbreviateMiddleNames(doctor?.name ?? "")}
              </Highlight>
              <Group gap={3}>
                <IconBriefcase size={16} color="var(--clr-gray-500)" />
                <Text
                  fz={13}
                  inherit
                  span
                  c="dimmed"
                  truncate
                  className={classes["doctor-specialization"]}>
                  {doctor?.specialization}
                </Text>
              </Group>
            </Stack>
          </Group>

          {doctor?.specialties?.length ? (
            <Group gap={5} mt={"1rem"}>
              {specialties.slice(0, 3)?.map(specialty => (
                <Badge key={specialty} variant="outline" fw={500}>
                  <Highlight highlight={search || ""} inherit tt="capitalize">
                    {specialty}
                  </Highlight>
                </Badge>
              ))}
              {specialties.length > 3 && (
                <Tooltip
                  label={specialties.slice(3).join(", ")}
                  events={{ hover: true, focus: true, touch: true }}
                  // * If a user attempts to click on tooltip, it navigates to doctor detail page :(
                  //* Stopping propagation on touch and click events, don't work :(
                  // onTouchStart={e => {
                  //   e.stopPropagation();
                  // }}
                  withArrow
                  multiline
                  maw={300}
                  ta="center">
                  <Badge variant="outline" tt="lowercase" fw={500} color="gray">
                    {t("x-more", { amount: specialties.length - 3 })}
                  </Badge>
                </Tooltip>
              )}
            </Group>
          ) : null}

          <Group justify="space-between" gap="xs" mt={"1rem"}>
            <Text fz={13} c="dimmed">{t("next-availability")}:</Text>
            <Text fz={13} c="dimmed" fw={600} ta="right">
              {nextAvailabilityFormatted || t("not-available")}
            </Text>
          </Group>

          <Group justify="space-between" gap="xs" mt={"0.5rem"}>
            <Text fz={13} c="dimmed">{t("licensed-in")}:</Text>
            <Text fz={13} c="dimmed" fw={600} ta="right">{licensedCountryNames}</Text>
          </Group>

          <Group justify="space-between" gap="xs" mt={"0.5rem"}>
            <Text fz={13} c="dimmed" tt="capitalize">{t("languages")}:</Text>
            <Text fz={13} c="dimmed" fw={600} ta="right" tt="capitalize">
              {doctor?.languages
                ?.map(lang => t(lang as TranslationKey))
                .join(", ")}
            </Text>
          </Group>

          {shouldShowRating ? (
            <Group justify="space-between" gap="xs" mt={"0.5rem"}>
              <Text fz={13} c="dimmed">
                {t("rating")}
              </Text>
              <Group gap={5}>
                <Text fz={13} c="dimmed" fw={600} ta="right">
                  {ratingSummary?.average?.toFixed(1)}
                </Text>
                <IconStarFilled
                  size={18}
                  color="var(--mantine-color-yellow-5)"
                />
                <Text fz={12} c="dimmed">
                  ({t("x-reviews", { amount: ratingSummary?.count ?? 0 })})
                </Text>
              </Group>
            </Group>
          ) : null}

          <Badge
            mt="1rem"
            w="100%"
            // bg="hsla(180, 50%, 95%, 1)"
            // bg="primary.0"
            className={classes["years-of-experience-badge"]}
            c="primary.6"
            tt="capitalize"
            py={"xs"}
            fw={600}>
            <Group gap={5}>
              <IconTrophyFilled size={16} />
              {t("x-years-of-experience", {
                amount: doctor?.yearsOfExperience,
              })}
            </Group>
          </Badge>

          {lowestPrice && (
            <>
              <Divider my="sm" />
              <Group justify="space-between" align="center" wrap="nowrap">
                <Stack gap={2}>
                  <Text fz={12} c="dimmed">
                    {t("starting-from")}
                  </Text>
                  <Text fz={18} fw={600}>
                    {formatCurrency(lowestPrice, i18n.language, currency)}
                  </Text>
                  <Text fz={13} c="dimmed">
                    {doctor?.consultationModes?.map(mode =>
                      t(mode as TranslationKey)
                    ).join("/")}
                  </Text>
                </Stack>
                <Button
                  component="span"
                  radius="sm"
                  px="lg"
                  py="xs"
                  fw={600}
                  fz={13}>
                  {t("book-now")}
                </Button>
              </Group>
            </>
          )}
        </>
      )}
    </Paper>
  );
};
